import { ArrowLeft, Zap, Activity, Scale, User, Target } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate } from "react-router-dom";

const BiteBalanceDetail = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 border-b border-border bg-background">
        <div className="flex items-center gap-2 p-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-base font-semibold text-foreground">
            Bite Balance Score
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* What is BBS */}
        <Card className="rounded-xl border border-border/50 bg-gradient-to-br from-primary/5 to-primary/10 p-5 shadow-sm">
          <h2 className="mb-2 text-lg font-bold text-foreground">
            What is BBS?
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            The Bite Balance Score is a single daily metric that combines your
            meal quality, body composition, and physical activity into one
            easy-to-read number from 0 to 100.
          </p>
        </Card>

        {/* GL Penalty */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Zap className="text-warning h-5 w-5" />
            <h3 className="text-base font-bold text-foreground">
              Glycemic Load Penalty
            </h3>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            This factor measures how much your meals may raise blood sugar. It
            considers net carbs relative to fiber intake, and is moderated by
            protein and fat — both of which slow glucose absorption. A higher
            penalty lowers your score.
          </p>
        </Card>

        {/* Gender Factor */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <User className="text-chart-2 h-5 w-5" />
            <h3 className="text-base font-bold text-foreground">
              Gender Factor
            </h3>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Biological sex influences how the body processes glucose and stores
            energy. This factor adjusts the score to account for known metabolic
            differences between males and females.
          </p>
        </Card>

        {/* BMI Factor */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Scale className="text-chart-3 h-5 w-5" />
            <h3 className="text-base font-bold text-foreground">BMI Factor</h3>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Body mass index reflects body composition and influences insulin
            sensitivity. A higher BMI amplifies the glycemic load penalty, while
            a lower BMI reduces it.
          </p>
        </Card>

        {/* Activity Bonus */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Activity className="text-chart-2 h-5 w-5" />
            <h3 className="text-base font-bold text-foreground">
              Activity Bonus
            </h3>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Physical activity helps your body use glucose more efficiently. This
            bonus is earned from your daily movement data — including average
            intensity, peak effort, and overall activity load. More movement
            means a higher score boost.
          </p>
        </Card>

        {/* AZM Proxy */}
        <Card className="bg-chart-2/5 rounded-xl border border-border/50 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Target className="text-chart-2 h-5 w-5" />
            <h3 className="text-sm font-bold text-foreground">
              Activity Zone Minutes
            </h3>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            When detailed heart-rate data isn't available, your Activity Zone
            Minutes from your wearable are used as a proxy to estimate exercise
            intensity and calculate your activity bonus.
          </p>
        </Card>

        {/* Score Ranges */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Score Ranges</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent</span>
              <span className="text-chart-2 font-semibold">80 – 100</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold text-primary">60 – 79</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Caution</span>
              <span className="text-warning font-semibold">40 – 59</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Poor</span>
              <span className="font-semibold text-destructive">0 – 39</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BiteBalanceDetail;
